import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import 'mapbox-gl/dist/mapbox-gl.css'
import mapboxgl from 'mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import { LngLatLike } from 'mapbox-gl'
// first one should be loaded asap
import warm27 from './GeoJsons/warm27C_poly.json'

const geoJsonNamesToFetch = [
  'warm15C_poly.json',
  'warm18C_poly.json',
  'warm20C_poly.json',
  'warm24C_poly.json',
  undefined,
  'warm36C_poly.json',
]
const geoJsons = [undefined, undefined, undefined, undefined, warm27, undefined]
export const MapBoxMap = ({
  init,
  flyTo,
  disableRotation,
  getMap,
  currentTemperatureIndex = 5,
  colorMode,
  lang
}: {
  init?: {
    lng?: number
    lat?: number
    zoom?: number
    minZoom?: number
    maxZoom?: number
    style?: string
    accessToken?: string
    showNavigationControl?: boolean
  }
  flyTo?: mapboxgl.FlyToOptions | undefined
  disableRotation?: boolean
  getMap?: (map: mapboxgl.Map) => void
  currentTemperatureIndex?: number
  colorMode?: 'dark' | 'light'
  lang: "de" | "fr"
}) => {
  const initDefaults = {
    center: [5.119415015764698, 22.505510671513136] as LngLatLike,
    // zoom: 1.96,
    style:
      colorMode === 'dark'
        ? 'mapbox://styles/ta-interaktiv/clk16xn1b00cw01pk9o857kal'
        : 'mapbox://styles/ta-interaktiv/cljzp8nfp008601pe3qsa0tiq',
    accessToken:
      'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg',
    showNavigationControl: true,
    scrollZoom: false,
    gestureHandling: true,
    touchZoomRotate: true,
    language: lang === "fr" ? "fr" : "gsw",
    //   dragRotate: true,
  }
  const [isFullScreen, setIsFullScreen] = useState(false)
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map>()
  const initWithDefaults = { ...initDefaults, ...init }

  useEffect(() => {
    if (!map.current) return // wait for map to initialize
    map.current.resize()
  }, [isFullScreen])

  useEffect(() => {
    if (!flyTo) return // wait for map to initialize
    map.current && map.current.flyTo(flyTo)
  }, [flyTo])
  useEffect(() => {
    if (!getMap) return // wait for map to initialize
    map.current && getMap(map.current)
  }, [getMap])
  useEffect(() => {
    if (!map.current) return // wait for map to initialize

    // check if map styles is loaded
    if (map.current.isStyleLoaded()) {
      // make any other layer transparent
      geoJsons.forEach((geoJson, index) => {
        map?.current?.setPaintProperty(
          'warm-layer' + index,
          'fill-opacity',
          index === currentTemperatureIndex ? 0.5 : 0
        )
      })
    }
  }, [map, currentTemperatureIndex])
  const addAllLayers = () => {
    // Add all the Layers
    geoJsons.forEach((geoJson, index) => {
      if (!geoJson) return
      !map?.current?.getSource('geoJson' + index) &&
        map?.current?.addSource('geoJson' + index, {
          type: 'geojson',
          data: geoJson as any,
        })
      const layers = map?.current?.getStyle().layers
      // Find the index of the first symbol layer in the map style.
      let firstSymbolId
      if (layers) {
        for (const layer of layers) {
          if (layer.type === 'symbol') {
            firstSymbolId = layer.id
            break
          }
        }
      }
      !map?.current?.getLayer('warm-layer' + index) &&
        map?.current?.addLayer(
          {
            id: 'warm-layer' + index,
            type: 'fill',
            source: 'geoJson' + index,
            paint: {
              'fill-color': '#D3343F',
              'fill-opacity': index === currentTemperatureIndex ? 0.6 : 0,
            },
          },
          firstSymbolId
        )
    })
  }
  useEffect(() => {
    if (map.current) return // initialize map only once

    mapboxgl.accessToken =
      'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'
    map.current = new mapboxgl.Map({
      container: mapContainer.current as HTMLElement,
      ...initDefaults,
    })
    if (map.current) {
      // zoom to bounds
      map.current?.fitBounds(
        [
          [-103.65950390621524, -29.986287479353507], // southwestern corner of the bounds
          [146.0993046322479, 38.44926896975991], // northeastern corner of the bounds
        ],
        {
          padding: 0,
        }
      )
    }
    map.current.on('click', (e) => {
      console.log('click', e.lngLat)
    })

    map.current.on('style.load', function () {
      addAllLayers()
    })
    initWithDefaults.showNavigationControl &&
      map.current.addControl(new mapboxgl.NavigationControl())

    if (disableRotation) {
      // disable map rotation using right click + drag
      map.current.dragRotate.disable()

      // disable map rotation using touch rotation gesture
      map.current.touchZoomRotate.disableRotation()
    }
    // fetch rest of the geoJsons at runtime
    Promise.all(
      geoJsonNamesToFetch.map((geoJsonName, index) => {
        // we already have 27° it will be undefined
        if (!geoJsonName) return
        const basePath =
          'https://interaktiv-mf.tagesanzeiger.ch/2023/global-temperature-map/'
        return fetch(basePath + 'geoJsons/' + geoJsonName)
          .then((res) => res.json())
          .then((newGeoJson) => {
            geoJsons[index] = newGeoJson
          })
      })
    ).then(() => {
      addAllLayers()
    })
  })

  return (
    <MapRoot rotationDisabled={disableRotation}>
      <MapBoxMapRoot
        isFullScreen={isFullScreen}
        ref={mapContainer}
        className='map-container'
      ></MapBoxMapRoot>
      <Overlay>
        {/* <FullScreenButton
          hasControls={initWithDefaults.showNavigationControl}
          onClick={() => setIsFullScreen(!isFullScreen)}
        >
          <FullScreenIcon />
          {isFullScreen ? 'Schliessen' : 'Vollbild'}
        </FullScreenButton> */}
      </Overlay>
    </MapRoot>
  )
}
const MapRoot = styled.div<{ rotationDisabled: boolean | undefined }>`
  position: relative;
  font-size: 12px;
  .mapboxgl-canvas {
    cursor: crosshair;
  }
  .mapboxgl-ctrl-compass {
    display: ${(props) => (props.rotationDisabled ? 'none' : 'unset')};
  }
`
const MapBoxMapRoot = styled.div<{ isFullScreen: boolean }>`
  min-height: 50vh;
  @media (min-width: 768px) {
    min-height: 60vh;
  }
`
const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`

const FullScreenButton = styled.button<{ hasControls: boolean }>`
  pointer-events: all;
  background-color: var(--brandblue-main);
  color: #fff;
  padding: 10px;
  font-size: 15px;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: ${(props) => (props.hasControls ? '10px' : '10px')};
  bottom: 40px;
  border: 0;

  z-index: 4000;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: scale(1.01);
    cursor: pointer;
    background-color: var(--red-main);
  }
  svg {
    margin-right: 5px;
  }
`

const FullScreenIcon = () => (
  <svg
    role='button'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M5 19V14H7V17H10V19H5ZM5 10V5H10V7H7V10H5ZM14 19V17H17V14H19V19H14ZM17 10V7H14V5H19V10H17Z'
      fill='currentColor'
    />
  </svg>
)
